import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useTranslation } from "i18n-fe";
import ShowSummaryCard from "./ShowSummaryCard";
import PageBreadcrumbs from "./PageBreadcrumbs";
import { format } from "date-fns";

/**
 * Header component for the show detail page containing breadcrumbs and summary cards
 * @param {Object} props
 * @param {Object} props.show - The show data object
 */
export default function ShowHeader({ show }) {
  const { t } = useTranslation("common");

  // Dummy data for summary cards
  const entries = {
    total: 96,
    paid: 39,
    open: 57,
    imported: 39,
  };
  const horses = 39;
  const riders = 37;
  const availableBalance = "$105.00";
  const grossReceipts = "$5,159.00";

  // Dummy data for show info
  const showInfo = {
    showOpen: "2025-05-09",
    showClose: "2025-05-11",
    entriesOpen: "2025-04-01",
    entriesClose: "2025-05-02",
    lateEntries: true,
    lateEntriesClose: "2025-05-05",
    manager: "John Smith",
    managerEmail: "john.smith@email.com",
    secretary: "Jane Doe",
    secretaryEmail: "jane.doe@email.com",
    location: "Majestic Farm, Batavia, OH",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MM/dd/yyyy");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  const formatDates = () => {
    if (!show.showTime?.open || !show.showTime?.close) return "-";
    return `${formatDate(show.showTime.open)} - ${formatDate(
      show.showTime.close
    )}`;
  };

  const formatRevenue = () => {
    if (typeof show.revenue !== "number") return "$0";
    return `$${show.revenue.toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "success";
      case "CLOSED":
        return "error";
      case "PREVIEW":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      <PageBreadcrumbs
        items={[
          { label: t("shows.list.title"), href: "/" },
          { label: show.name },
        ]}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          gap: 2,
          mb: 3,
          overflowX: "auto",
          pb: 1,
        }}
      >
        {/* Entries Card */}
        <ShowSummaryCard
          label="Entries"
          content={
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h5">{entries.total}</Typography>
                <Typography variant="body2">Total</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Paid</Typography>
                <Typography variant="body2">{entries.paid}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Open</Typography>
                <Typography variant="body2">{entries.open}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Imported</Typography>
                <Typography variant="body2">{entries.imported}</Typography>
              </Box>
            </Box>
          }
        />
        {/* Horses & Riders Card */}
        <ShowSummaryCard
          label="Horses & Riders"
          content={
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Horses</Typography>
                <Typography variant="h6">{horses}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Riders</Typography>
                <Typography variant="h6">{riders}</Typography>
              </Box>
            </Box>
          }
        />
        {/* Balances Card */}
        <ShowSummaryCard
          label="Balances"
          content={
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Available</Typography>
                <Typography variant="h6">{availableBalance}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Gross</Typography>
                <Typography variant="h6">{grossReceipts}</Typography>
              </Box>
            </Box>
          }
        />
        {/* Show Info Card */}
        <ShowSummaryCard
          label="Show Info"
          content={
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Show Dates</Typography>
                <Typography variant="body2">
                  {formatDate(show.showTime?.open)} –{" "}
                  {formatDate(show.showTime?.close)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Entry Period</Typography>
                <Typography variant="body2">
                  {formatDate(show.entryTime?.open)} –{" "}
                  {formatDate(show.entryTime?.close)}
                </Typography>
              </Box>
              {show.lateEntryTime?.close && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">Late Entry Close</Typography>
                  <Typography variant="body2">
                    {formatDate(show.lateEntryTime.close)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Show Manager</Typography>
                <Typography variant="body2">
                  {show.showManager?.fullName && show.showManager?.email ? (
                    <a
                      href={`mailto:${show.showManager.email}`}
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {show.showManager.fullName}
                    </a>
                  ) : (
                    "-"
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Secretary</Typography>
                <Typography variant="body2">
                  {show.showSecretary?.fullName && show.showSecretary?.email ? (
                    <a
                      href={`mailto:${show.showSecretary.email}`}
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {show.showSecretary.fullName}
                    </a>
                  ) : (
                    "-"
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Location</Typography>
                <Typography variant="body2">
                  {show.grounds?.city && show.grounds?.stateProvinceRegion
                    ? `${show.grounds.city}, ${show.grounds.stateProvinceRegion}`
                    : show.grounds?.city ||
                      show.grounds?.stateProvinceRegion ||
                      "-"}
                </Typography>
              </Box>
            </Box>
          }
        />
      </Box>
    </Box>
  );
}
